/* Benji widget styles. Scoped under #ccb-benji-root to avoid colliding with
 * Bricks Builder / theme CSS. Tokens align with bricks-child/_primitives.css
 * (the external bot's design system in the design README). */

#ccb-benji-root {
  /* === Colour tokens (external bot palette) === */
  --ccb-bx-black: #0E0901;
  --ccb-bx-white: #EFEAE5;
  --ccb-bx-pure: #FFFFFF;
  --ccb-bx-gold: #FFAA00;
  --ccb-bx-gold-1000: #B57200;
  --ccb-bx-gold-50: #FFF7E6;
  --ccb-bx-gold-400: #FFCC66;
  --ccb-bx-brown-100: #DFD5CC;
  --ccb-bx-brown-700: #7E5633;
  --ccb-bx-grey-700: #56574A;
  --ccb-bx-grey-800: #414233;
  --ccb-bx-success: #5BB85B;

  --ccb-bx-shadow-panel: 0 24px 60px rgba(0, 0, 0, 0.45), 0 4px 12px rgba(0, 0, 0, 0.2);
  --ccb-bx-shadow-launcher: 0 12px 28px rgba(0, 0, 0, 0.45), inset 0 -3px 0 rgba(0, 0, 0, 0.08);

  position: fixed;
  z-index: 2147483600; /* high but below browser dev-tools */
  pointer-events: none; /* container is invisible; children opt-in */
}

#ccb-benji-root * {
  box-sizing: border-box;
}

#ccb-benji-root, #ccb-benji-root * {
  font-family: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* === Launcher === */

.ccb-bx-launcher {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: flex-end;
  gap: 14px;
  pointer-events: none;
}

/* Host page opened a modal / full-screen nav. Fade the launcher out rather
   than unmounting it, so the variant-C teaser rotation and variant-B mount
   timers keep their state and don't restart when the overlay closes. */
.ccb-bx-launcher--suppressed {
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px) scale(0.94);
  transition: opacity 160ms ease, transform 160ms ease, visibility 0s linear 160ms;
}

.ccb-bx-launcher--suppressed .ccb-bx-launcher__bubble,
.ccb-bx-launcher--suppressed .ccb-bx-launcher__btn {
  pointer-events: none;
}

.ccb-bx-launcher:not(.ccb-bx-launcher--suppressed) {
  transition: opacity 200ms ease, transform 200ms ease;
}

@media (prefers-reduced-motion: reduce) {
  .ccb-bx-launcher,
  .ccb-bx-launcher--suppressed {
    transition: none;
    transform: none;
  }
}

.ccb-bx-launcher__bubble {
  background: var(--ccb-bx-white);
  color: var(--ccb-bx-black);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  padding: 9px 14px;
  border-radius: 14px 14px 4px 14px;
  max-width: 240px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  pointer-events: auto;
  margin-bottom: 16px;
  cursor: pointer;
}

.ccb-bx-launcher__btn {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
  border: none;
  cursor: pointer;
  font-family: 'Bowlby One', system-ui, sans-serif;
  font-size: 30px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  box-shadow: var(--ccb-bx-shadow-launcher);
  transition: transform 0.15s ease;
}
.ccb-bx-launcher__btn::before {
  content: "";
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 170, 0, 0.45), rgba(255, 170, 0, 0) 70%);
  filter: blur(6px);
  pointer-events: none;
}
.ccb-bx-launcher__btn::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 6px rgba(255, 170, 0, 0.18);
  pointer-events: none;
}
.ccb-bx-launcher__btn:hover {
  transform: translateY(-1px);
}
/* Avatar sizing inside the launcher pill — fills the gold circle with a small
   inset so the face doesn't graze the rim. Per Sean's direction the launcher
   Benji faces west, so we flip the natural east-facing SVG horizontally. */
.ccb-bx-launcher__btn > svg {
  width: 72%;
  height: 72%;
  transform: scaleX(-1);
}
/* Header avatar shows Benji facing east — the SVG's natural direction. */

/* === Launcher microinteraction A/B/C variants ===
   Orthogonal to the CTA copy A/B test — each visitor gets one A/B/C
   variant for the lifetime of their session. Hover/focus pauses the
   animation so the dwelling visitor isn't distracted by motion. Respects
   prefers-reduced-motion. */

/* Variant A — ripple + gentle bobble */
.ccb-bx-launcher__btn--interaction-a {
  animation: ccb-bx-launcher-bobble 4.2s ease-in-out infinite;
}
.ccb-bx-launcher__btn--interaction-a::after {
  animation: ccb-bx-launcher-ripple 3.6s ease-out infinite;
}
.ccb-bx-launcher__btn--interaction-a:hover,
.ccb-bx-launcher__btn--interaction-a:focus-visible {
  animation-play-state: paused;
}
.ccb-bx-launcher__btn--interaction-a:hover::after,
.ccb-bx-launcher__btn--interaction-a:focus-visible::after {
  animation-play-state: paused;
}
@keyframes ccb-bx-launcher-ripple {
  0%   { box-shadow: 0 0 0  6px rgba(255, 170, 0, 0.18); }
  60%  { box-shadow: 0 0 0 18px rgba(255, 170, 0, 0); }
  100% { box-shadow: 0 0 0  6px rgba(255, 170, 0, 0.18); }
}
@keyframes ccb-bx-launcher-bobble {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-3px); }
}

/* Variant B — personality wiggle. Targets the SVG child. The avatar SVG
   is already scaleX(-1) for west-facing, so each keyframe composes that
   with the rotation to avoid un-flipping. First-mount runs at 4.5s once,
   then settles to the 7s steady-state. */
.ccb-bx-launcher__btn--interaction-b > svg {
  animation: ccb-bx-launcher-wiggle 7s ease-in-out infinite;
}
.ccb-bx-launcher__btn--interaction-b.ccb-bx-launcher__btn--first-mount > svg {
  animation: ccb-bx-launcher-wiggle 4.5s ease-in-out infinite;
}
.ccb-bx-launcher__btn--interaction-b:hover > svg,
.ccb-bx-launcher__btn--interaction-b:focus-visible > svg {
  animation-play-state: paused;
}
@keyframes ccb-bx-launcher-wiggle {
  0%, 86%, 100% { transform: scaleX(-1) rotate(0); }
  90%           { transform: scaleX(-1) rotate(-5deg); }
  93%           { transform: scaleX(-1) rotate(5deg); }
  96%           { transform: scaleX(-1) rotate(-3deg); }
}

/* Variant C — rotating speech-bubble teaser. Cycling is JS-driven (text
   content changes). The CSS just provides the fade transition so the
   swap doesn't pop. */
.ccb-bx-launcher__bubble--rotating {
  transition: opacity 0.25s ease;
}
.ccb-bx-launcher__bubble--rotating-hidden {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ccb-bx-launcher__btn--interaction-a,
  .ccb-bx-launcher__btn--interaction-a::after,
  .ccb-bx-launcher__btn--interaction-b > svg {
    animation: none !important;
  }
  .ccb-bx-launcher__bubble--rotating {
    transition: none !important;
  }
}

@media (max-width: 640px) {
  .ccb-bx-launcher { bottom: 16px; right: 20px; }
}

/* === Chat window === */

.ccb-bx-window {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 400px;
  max-width: calc(100vw - 32px);
  height: min(640px, calc(100vh - 48px));
  background: var(--ccb-bx-white);
  border-radius: 12px;
  box-shadow: var(--ccb-bx-shadow-panel);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  animation: ccb-bx-window-in 200ms ease-out;
}

@keyframes ccb-bx-window-in {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}

@media (max-width: 640px) {
  .ccb-bx-window {
    /* iOS notch / Dynamic Island: top must be below env(safe-area-inset-top),
       otherwise the chat header gets clipped behind the status bar. Fallback
       44px matches non-notched iPhones; max() picks whichever is larger. */
    top: max(44px, env(safe-area-inset-top));
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 20px 20px 0 0;
  }
}

/* === Header === */

.ccb-bx-header {
  background: var(--ccb-bx-black);
  color: var(--ccb-bx-white);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}
.ccb-bx-header__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Bowlby One', system-ui, sans-serif;
  font-size: 20px;
  overflow: hidden;
}
.ccb-bx-header__avatar > svg {
  width: 78%;
  height: 78%;
}
.ccb-bx-header__meta { flex: 1; }
.ccb-bx-header__name {
  font-family: 'Bowlby One', system-ui, sans-serif;
  font-size: 16px;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  line-height: 1;
}
.ccb-bx-header__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #9e8f6e;
  margin-top: 4px;
}
.ccb-bx-header__pip {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ccb-bx-success);
  display: inline-block;
}
.ccb-bx-header__close {
  background: transparent;
  border: none;
  color: var(--ccb-bx-white);
  cursor: pointer;
  font-size: 18px;
  padding: 4px 8px;
}
/* Team-only "New chat" reset button — only rendered when the preview cookie
   is set, so real prospects never see it mid-conversation. Subtle gold pill
   so it reads as a tool, not a primary CTA. */
.ccb-bx-header__newchat {
  background: rgba(255, 170, 0, 0.18);
  border: 1px solid rgba(255, 170, 0, 0.55);
  color: var(--ccb-bx-gold);
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 9px;
  border-radius: 999px;
  margin-right: 4px;
}
.ccb-bx-header__newchat:hover {
  background: rgba(255, 170, 0, 0.3);
}

/* === Messages area === */

.ccb-bx-thread {
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--ccb-bx-white);
}

.ccb-bx-bot {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 88%;
}
.ccb-bx-bot__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Bowlby One', system-ui, sans-serif;
  font-size: 14px;
  flex-shrink: 0;
  /* Pull the SVG-rendered face up + clip the overflow so the chin doesn't
     fall outside the gold circle. The Benji avatar's design hangs slightly
     below center so a small Y offset re-aligns the face. */
  overflow: hidden;
}
.ccb-bx-bot__avatar > svg {
  width: 70%;
  height: 70%;
}
.ccb-bx-bot__bubble {
  background: var(--ccb-bx-pure);
  color: var(--ccb-bx-black);
  border: 1px solid var(--ccb-bx-brown-100);
  border-radius: 4px 14px 14px 14px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
}
/* When the bubble holds only the typing-dots indicator (no text yet), shrink
   it to hug its 3-dot content instead of stretching to fill the wrapper.
   Otherwise an empty bubble takes up the same wide area as a long reply,
   which reads as "Benji wrote nothing" rather than "Benji is typing." The
   border is removed so the dots float in a pill of pure surface — the thin
   brown rim around three dots reads as visual noise. */
.ccb-bx-bot__bubble--typing {
  display: inline-block;
  padding: 12px 16px;
  border-color: transparent;
}

.ccb-bx-user {
  align-self: flex-end;
  max-width: 78%;
  background: var(--ccb-bx-black);
  color: var(--ccb-bx-white);
  border-radius: 14px 14px 4px 14px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
}

.ccb-bx-suggestions {
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  max-width: 90%;
}
.ccb-bx-suggestions__lbl {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ccb-bx-brown-700);
}
.ccb-bx-suggestions button {
  background: var(--ccb-bx-white);
  color: var(--ccb-bx-black);
  border: 1.5px solid var(--ccb-bx-black);
  border-radius: 14px 14px 4px 14px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}
.ccb-bx-suggestions button:hover { background: var(--ccb-bx-gold-50); }

/* Chip row Benji emits via [[chips: A | B | C]] sentinel. Sits left-aligned
   under the bot's bubble so it reads as "pick one of these options". Tap
   sends the chip text as the next user message immediately. */
.ccb-bx-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 6px;
}
.ccb-bx-chips button {
  background: var(--ccb-bx-pure);
  color: var(--ccb-bx-black);
  border: 1.5px solid var(--ccb-bx-brown-100);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.ccb-bx-chips button:hover {
  background: var(--ccb-bx-gold-50);
  border-color: var(--ccb-bx-gold);
}
.ccb-bx-chips button:active { transform: translateY(1px); }

/* Multi-select picks row Benji emits via [[picks: A | B | C]] sentinel. Tap
   accumulates labels into the composer so the prospect can mix multiple
   selections with free text and send when ready. Visually distinct from the
   single-select chips so the prospect understands "you can pick many." */
.ccb-bx-picks {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}
.ccb-bx-picks__lbl {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ccb-bx-brown-700);
}
.ccb-bx-picks__row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ccb-bx-picks button {
  background: var(--ccb-bx-pure);
  color: var(--ccb-bx-black);
  border: 1.5px solid var(--ccb-bx-brown-100);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.ccb-bx-picks button:hover {
  background: var(--ccb-bx-gold-50);
  border-color: var(--ccb-bx-gold);
}
.ccb-bx-picks button.is-active {
  background: var(--ccb-bx-black);
  color: var(--ccb-bx-white);
  border-color: var(--ccb-bx-black);
}
.ccb-bx-picks button:active { transform: translateY(1px); }

/* Date picker — rendered when Benji emits [[date]]. Uses native input so the
   OS picker shows up on mobile. The "Use date" button commits the chosen
   date (the native input's `change` event alone would fire constantly while
   the user is scrolling wheels). */
.ccb-bx-datepicker {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 8px;
  align-items: flex-start;
}
.ccb-bx-datepicker__lbl {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ccb-bx-brown-700);
}
.ccb-bx-datepicker__row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ccb-bx-picker__input-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.ccb-bx-picker__placeholder {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ccb-bx-brown-300, #bfab96);
  font-size: 14px;
  pointer-events: none;
  white-space: nowrap;
}
.ccb-bx-datepicker input[type="date"],
.ccb-bx-timepicker input[type="time"] {
  border: 1.5px solid var(--ccb-bx-brown-100);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 16px; /* iOS zoom-on-focus guard */
  font-family: inherit;
  background: var(--ccb-bx-pure);
  color: var(--ccb-bx-black);
  min-width: 160px;
}
.ccb-bx-datepicker input[type="date"]:focus,
.ccb-bx-timepicker input[type="time"]:focus {
  outline: 2px solid var(--ccb-bx-gold);
  outline-offset: -1px;
  border-color: var(--ccb-bx-gold);
}
.ccb-bx-datepicker__send {
  background: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
}
.ccb-bx-datepicker__send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.ccb-bx-datepicker__send:not(:disabled):hover {
  opacity: 0.9;
}

/* Inline `code` spans from the markdown renderer — kept subtle so they don't
   shout in a casual chat bubble. */
.ccb-bx-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.92em;
  background: rgba(14, 9, 1, 0.06);
  padding: 1px 5px;
  border-radius: 4px;
}

.ccb-bx-thumbs {
  display: flex;
  gap: 6px;
  padding: 8px 0 0 0;
  margin-left: 2px;
}
.ccb-bx-thumbs button {
  /* Brand brown SVG icons on a cream background — high contrast vs the old
     0.5-opacity emoji that washed out completely. Gold fill on active state. */
  background: transparent;
  border: 1px solid var(--ccb-bx-brown-100);
  border-radius: 8px;
  width: 32px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ccb-bx-brown-700);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.ccb-bx-thumbs button:hover {
  background: var(--ccb-bx-gold-50);
  border-color: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
}
.ccb-bx-thumbs button.active {
  background: var(--ccb-bx-gold-50);
  border-color: var(--ccb-bx-gold);
  color: var(--ccb-bx-gold);
}

.ccb-bx-typing {
  display: inline-flex;
  gap: 3px;
  padding: 8px 12px;
  background: var(--ccb-bx-pure);
  border: 1px solid var(--ccb-bx-brown-100);
  border-radius: 12px;
}
.ccb-bx-typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ccb-bx-grey-700);
  animation: ccb-bx-typing-bounce 1.2s infinite ease-in-out;
}
.ccb-bx-typing span:nth-child(2) { animation-delay: 0.15s; }
.ccb-bx-typing span:nth-child(3) { animation-delay: 0.3s; }

@keyframes ccb-bx-typing-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.6; }
  40%           { transform: scale(1);   opacity: 1; }
}

/* === Composer === */

.ccb-bx-composer {
  background: var(--ccb-bx-white);
  padding: 12px 14px 10px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  border-top: 1px solid var(--ccb-bx-brown-100);
}
.ccb-bx-composer textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--ccb-bx-brown-100);
  border-radius: 12px;
  padding: 10px 12px;
  /* iOS Safari zooms the viewport whenever a text input has font-size < 16px.
     Keep this at 16px (or larger) on every focusable input to prevent the
     zoom-in / zoom-out cycle when the keyboard opens and closes. */
  font-size: 16px;
  line-height: 1.4;
  background: var(--ccb-bx-pure);
  color: var(--ccb-bx-black);
  min-height: 40px;
  /* Auto-grows in JS (textarea onInput sets inline height up to this cap). */
  max-height: 140px;
  font-family: inherit;
  overflow-y: auto;
}
.ccb-bx-composer textarea::placeholder { color: #a59c8b; }
.ccb-bx-composer textarea:focus { outline: 2px solid var(--ccb-bx-gold); outline-offset: -1px; }
.ccb-bx-composer__send {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
  border: none;
  cursor: pointer;
  /* Center the inline SVG perfectly — the old `font-size: 16px` was for the
     stray "➤" glyph, which rendered tiny and off-center on most platforms. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  /* Slight nudge for the paper-plane glyph — its centroid sits a hair
     left/down of geometric center, so this trues it up visually. */
  padding-right: 2px;
  padding-top: 1px;
}
.ccb-bx-composer__send:disabled { opacity: 0.45; cursor: not-allowed; }

.ccb-bx-footer {
  background: var(--ccb-bx-white);
  text-align: center;
  font-size: 10px;
  color: var(--ccb-bx-brown-700);
  padding: 0 14px 10px;
  letter-spacing: 0.04em;
}

/* === Contact form === */

.ccb-bx-form {
  background: var(--ccb-bx-gold-50);
  border: 1px solid var(--ccb-bx-gold-400);
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ccb-bx-form__eyebrow {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ccb-bx-gold-1000);
}
.ccb-bx-form__title {
  font-family: 'Bowlby One', system-ui, sans-serif;
  font-size: 16px;
  text-transform: uppercase;
  color: var(--ccb-bx-black);
}
.ccb-bx-form__sub {
  font-size: 12px;
  color: var(--ccb-bx-grey-700);
}
.ccb-bx-form__summary {
  background: var(--ccb-bx-pure);
  border: 1.5px dashed var(--ccb-bx-gold-400);
  border-radius: 6px;
  padding: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 14px;
  font-size: 12px;
}
.ccb-bx-form__summary .row { display: flex; flex-direction: column; }
.ccb-bx-form__summary .row.full { grid-column: 1 / -1; }
.ccb-bx-form__summary .lbl {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ccb-bx-brown-700);
}
.ccb-bx-form__summary .val { color: var(--ccb-bx-black); font-weight: 500; }
.ccb-bx-form__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ccb-bx-form__field label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ccb-bx-brown-700);
}
.ccb-bx-form__field input,
.ccb-bx-form__field textarea {
  border: 1px solid var(--ccb-bx-brown-100);
  border-radius: 6px;
  padding: 8px 10px;
  /* iOS Safari viewport zoom — see .ccb-bx-composer textarea comment. */
  font-size: 16px;
  background: var(--ccb-bx-pure);
  font-family: inherit;
}
.ccb-bx-form__seg {
  display: flex;
  border: 1px solid var(--ccb-bx-brown-100);
  border-radius: 999px;
  overflow: hidden;
  background: var(--ccb-bx-pure);
}
.ccb-bx-form__seg button {
  flex: 1;
  background: transparent;
  border: none;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: var(--ccb-bx-black);
}
.ccb-bx-form__seg button.active {
  background: var(--ccb-bx-black);
  color: var(--ccb-bx-white);
}
.ccb-bx-form__consent {
  font-size: 10px;
  color: var(--ccb-bx-grey-700);
  line-height: 1.4;
}
.ccb-bx-form__submit {
  background: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
  border: none;
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
}
.ccb-bx-form__submit:disabled { opacity: 0.5; cursor: not-allowed; }
.ccb-bx-form__error {
  background: #ffe9e0;
  color: #6a1e10;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 12px;
}

/* === Submitted card === */

.ccb-bx-done {
  background: var(--ccb-bx-pure);
  border: 1px solid var(--ccb-bx-brown-100);
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(14, 9, 1, 0.06);
}
.ccb-bx-done__check {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ccb-bx-gold);
  color: var(--ccb-bx-black);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
}
.ccb-bx-done__title {
  font-family: 'Bowlby One', system-ui, sans-serif;
  font-size: 18px;
  text-transform: uppercase;
}
.ccb-bx-done__sub {
  font-size: 12px;
  color: var(--ccb-bx-grey-700);
}
.ccb-bx-done__steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 6px;
}
.ccb-bx-done__step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.ccb-bx-done__step .num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--ccb-bx-gold-50);
  color: var(--ccb-bx-gold-1000);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}
.ccb-bx-done__step .body {
  font-size: 12px;
  line-height: 1.45;
}
.ccb-bx-done__step .body strong {
  display: block;
  font-size: 13px;
  font-weight: 800;
  color: var(--ccb-bx-black);
}
